import * as THREE from 'three';
import {
  ACTION_MS,
  Bot,
  ITEMS,
  Match,
  Maze,
  SIGHTLINE_ROOMS,
  axisLabel,
  createRng,
  cross,
  doorKey,
  negate,
  rotationMs,
  type Action,
  type ActiveAction,
  type Item,
  type BotTier,
  type MatchEvent,
  type Orientation,
  type Rotation,
} from '@qoobd/rules';
import { audio } from '../audio/engine';
import { useGame, type SpectatorView } from './store';
import { countMatch } from './ping';
import { randomArmor } from './armor';
import type { RemoteMatch } from './remote';

export const PLAYER = 'player';
export const RIVAL = 'rival';
/** Display names in AI vs AI, where both seats are bots. */
export const WATCH_NAMES: Record<string, string> = { player: 'AMBER', rival: 'ICE' };
/** World units per room. */
export const ROOM_SIZE = 4;

export function roomCenter(maze: Maze, room: number, out = new THREE.Vector3()): THREE.Vector3 {
  const [x, y, z] = maze.coords(room);
  return out.set(x * ROOM_SIZE, y * ROOM_SIZE, z * ROOM_SIZE);
}

/** Camera-style basis: looks down -Z with +Y up, like three.js cameras. */
export function orientationQuat(o: Orientation, out = new THREE.Quaternion()): THREE.Quaternion {
  const r = cross(o.facing, o.up);
  const back = negate(o.facing);
  const m = new THREE.Matrix4().makeBasis(
    new THREE.Vector3(r[0], r[1], r[2]),
    new THREE.Vector3(o.up[0], o.up[1], o.up[2]),
    new THREE.Vector3(back[0], back[1], back[2]),
  );
  return out.setFromRotationMatrix(m);
}

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const REDUCED_MOTION = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface Pose {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
}

/**
 * Runs a local Player vs Bot match on top of the shared rules engine. The engine's clock is
 * "match time" (ms since the match started); the controller converts from performance.now().
 */
class GameController {
  match: Match | null = null;
  mode: 'play' | 'watch' | 'online' = 'play';
  /** The server's match during online play (also assigned to `match`, which it stands in for). */
  remote: RemoteMatch | null = null;
  private bots: Bot[] = [];
  /** Match clock: real time scaled by the spectator speed, accumulated so speed changes never jump. */
  private clock = 0;
  private lastReal = 0;
  private speed = 1;
  private lastSpectate = 0;
  private feedId = 0;
  /** Doors the player has passed through (the personal amber trail). */
  readonly trail = new Set<number>();
  visible = new Set<number>();
  path: number[] = [];
  /** The rival's rooms in order, for the share card and spectator trails. */
  rivalPath: number[] = [];
  explored = new Set<number>();

  now(): number {
    if (this.remote) return this.remote.now();
    const r = performance.now();
    this.clock += (r - this.lastReal) * this.speed;
    this.lastReal = r;
    return this.clock;
  }

  private resetClock(): void {
    this.clock = 0;
    this.lastReal = performance.now();
  }

  setSpeed(speed: 1 | 2): void {
    this.now(); // bank the time so far at the old speed
    this.speed = speed;
  }

  private newMatch(seed: number): Maze {
    const maze = Maze.generate(seed);
    const rng = createRng(seed ^ 0x5bd1e995);
    const pick = () => ITEMS[Math.floor(rng() * ITEMS.length)]!;
    this.match = new Match(maze, [
      { id: PLAYER, item: pick() },
      { id: RIVAL, item: pick(), orientation: { facing: [0, 0, 1], up: [0, 1, 0] } },
    ]);
    this.resetClock();
    this.trail.clear();
    this.path = [maze.spawns[0]];
    this.rivalPath = [maze.spawns[1]];
    this.explored = new Set([maze.spawns[0]]);
    return maze;
  }

  /** AI vs AI: two bots, no player input; the scene switches to the spectator dolly. */
  watch(tiers: [BotTier, BotTier], seed = Math.floor(Math.random() * 900_000) + 100_000): void {
    if (import.meta.env.PROD) countMatch();
    this.mode = 'watch';
    this.remote = null;
    const maze = this.newMatch(seed);
    this.speed = useGame.getState().watch.speed;
    this.bots = [new Bot(PLAYER, tiers[0], createRng(seed + 3), 1200), new Bot(RIVAL, tiers[1], createRng(seed + 7), 1500)];
    this.visible = maze.roomsWithin([maze.spawns[0]], 1);
    this.feedId = 0;
    const s = useGame.getState();
    const looks = createRng(seed ^ 0x2545f491);
    useGame.setState({
      mode: 'watch',
      watchArmor: randomArmor(looks),
      rivalArmor: randomArmor(looks),
      phase: 'playing',
      seed,
      result: null,
      toast: null,
      startedAt: performance.now(),
      watch: { ...s.watch, tiers, follow: PLAYER },
      spectator: { agents: {}, intercept: null, cloaked: false, feed: [] },
      sceneVersion: s.sceneVersion + 1,
    });
    this.spectate(true);
    audio.resetDread();
  }

  start(tier: BotTier, seed = Math.floor(Math.random() * 900_000) + 100_000): void {
    if (import.meta.env.PROD) countMatch();
    this.mode = 'play';
    this.remote = null;
    this.speed = 1;
    const maze = this.newMatch(seed);
    const playerItem = this.match!.agent(PLAYER)!.item;
    this.bots = [new Bot(RIVAL, tier, createRng(seed + 7), 1500)];
    const spawn = maze.spawns[0];
    this.visible = maze.roomsWithin([spawn], SIGHTLINE_ROOMS);
    const s = useGame.getState();
    useGame.setState({
      mode: 'play',
      rivalArmor: randomArmor(createRng(seed ^ 0x2545f491)),
      spectator: null,
      phase: 'playing',
      rivalVisible: false,
      tier,
      seed,
      item: playerItem,
      steps: 0,
      actions: 0,
      bonks: 0,
      startedAt: null,
      lock: null,
      result: null,
      toast: null,
      jammed: false,
      sceneVersion: s.sceneVersion + 1,
    });
    this.syncHud();
    audio.resetDread();
  }

  /** Online 1v1: render the server's match for this player. Called once the first state arrives. */
  startOnline(remote: RemoteMatch): void {
    if (import.meta.env.PROD) countMatch();
    this.mode = 'online';
    this.remote = remote;
    this.match = remote as unknown as Match; // stands in for Match: same questions, server answers
    this.bots = [];
    this.trail.clear();
    const spawn = remote.agent(PLAYER)!.room;
    this.path = [spawn];
    this.rivalPath = [];
    this.explored = new Set([spawn]);
    this.visible = remote.maze.roomsWithin([spawn], SIGHTLINE_ROOMS);
    const s = useGame.getState();
    useGame.setState({
      mode: 'online',
      spectator: null,
      phase: 'playing',
      rivalVisible: false,
      seed: 0,
      item: remote.agent(PLAYER)!.item,
      steps: 0,
      actions: 0,
      bonks: 0,
      startedAt: performance.now(),
      lock: null,
      result: null,
      toast: null,
      jammed: false,
      sceneVersion: s.sceneVersion + 1,
    });
    this.syncHud();
    audio.resetDread();
  }

  /** The server refused an input (a lock we hadn't seen yet, nothing to seal, ...). */
  rejected(reason: string): void {
    if (reason === 'locked') {
      useGame.setState((s) => ({ lockFlash: s.lockFlash + 1 }));
      audio.tick();
    } else if (reason === 'nothingToSeal') {
      audio.tick();
      this.toast('NOTHING TO SEAL', 'SLAM IT RIGHT AFTER YOU PASS A DOOR');
    } else if (reason === 'noCharge') {
      audio.tick();
      this.toast('NO CHARGE', 'FIND A SUPPLY CACHE');
    }
  }

  /** The server ended the online match; items are revealed now. */
  finishOnline(over: { result: { winner: string; loser: string; reason: 'core' | 'encounter'; at: number }; items: { player: Item; rival: Item }; forfeit: boolean; elapsedMs: number }): void {
    const m = this.remote;
    if (!m) return;
    const me = m.agent(PLAYER);
    if (over.result.winner === PLAYER) audio.fanfare();
    else audio.defeat();
    const s = useGame.getState();
    useGame.setState({
      phase: 'over',
      lock: null,
      result: {
        ...over.result,
        playerItem: over.items.player,
        rivalItem: over.items.rival,
        elapsedMs: over.elapsedMs,
        steps: me?.steps ?? s.steps,
        actions: me?.actions ?? s.actions,
        bonks: me?.bonks ?? s.bonks,
        explored: this.explored.size,
        tier: s.tier,
        seed: 0,
        forfeit: over.forfeit,
      },
    });
  }

  /** A player input from keyboard, touch or buttons. Rejected inputs during a lock flash the HUD. */
  input(action: Action): void {
    const m = this.match;
    const state = useGame.getState();
    if (!m || state.phase !== 'playing' || this.mode === 'watch') return;
    if (action.kind === 'swap' && action.item === m.agent(PLAYER)!.item) return;
    const res = m.input(PLAYER, action, this.now());
    if (!res.ok) {
      if (res.reason === 'locked') {
        useGame.setState({ lockFlash: state.lockFlash + 1 });
        audio.tick();
      } else if (res.reason === 'noCharge') {
        audio.tick();
        this.toast(action.kind === 'emp' ? 'NO EMP' : 'NO BULKHEAD', 'FIND A SUPPLY CACHE');
      } else if (res.reason === 'nothingToSeal') {
        audio.tick();
        this.toast('NOTHING TO SEAL', 'SLAM IT RIGHT AFTER YOU PASS A DOOR');
      }
      return;
    }
    if (state.startedAt === null) useGame.setState({ startedAt: performance.now() });
    this.handle(m.takeEvents());
  }

  /** Advance the match and the bot. Called every frame. */
  tick(): void {
    const m = this.match;
    if (!m || useGame.getState().phase !== 'playing') return;
    const t = this.now();
    m.advance(t);
    for (const bot of this.bots) {
      const action = bot.think(m, t);
      if (action) m.input(bot.id, action, t);
    }
    if (this.mode === 'online') {
      this.handle(m.takeEvents());
      if (this.remote?.ready) this.syncSensors();
      return;
    }
    if (this.mode === 'watch') {
      this.handleWatch(m.takeEvents());
      this.spectate(false);
      return;
    }
    this.handle(m.takeEvents());
    this.syncSensors();
  }

  /** Spectator events: a broadcast feed, a few sounds, the result. Spectators see every item. */
  private handleWatch(events: MatchEvent[]): void {
    const m = this.match!;
    const name = (id: string) => WATCH_NAMES[id] ?? id;
    const feed: SpectatorView['feed'] = [];
    const say = (text: string, side: string | null = null) => feed.push({ id: ++this.feedId, text, side });
    for (const e of events) {
      if (e.type === 'arrived') {
        (e.agent === PLAYER ? this.path : this.rivalPath).push(e.room);
        if (e.agent === useGame.getState().watch.follow) this.refocus();
      } else if (e.type === 'swapped') say(`${name(e.agent)} swapped to ${m.agent(e.agent)!.item.toUpperCase()}`, e.agent);
      else if (e.type === 'empFired') {
        audio.empFire();
        say(`${name(e.agent)} fired an EMP`, e.agent);
      } else if (e.type === 'jammed') say(`${name(e.agent)}'s sensors are jammed`, e.agent);
      else if (e.type === 'doorSealed') {
        audio.seal();
        say(`${name(e.agent)} sealed a door`, e.agent);
      } else if (e.type === 'cachePicked') {
        say(`${name(e.agent)} grabbed ${e.kind === 'emp' ? 'an EMP' : 'a bulkhead'}`, e.agent);
        this.bumpScene();
      } else if (e.type === 'cloaked') {
        audio.shimmer();
        say('Same items: both cloaked');
      } else if (e.type === 'doorwayClash') say('Doorway clash!');
      else if (e.type === 'matchOver') {
        audio.fanfare();
        const r = e.result;
        say(r.reason === 'core' ? `${name(r.winner)} claimed the core` : `${name(r.winner)} won the encounter`, r.winner);
        const s = useGame.getState();
        const w = m.agent(PLAYER)!;
        useGame.setState({
          phase: 'over',
          result: {
            ...r,
            playerItem: w.item,
            rivalItem: m.agent(RIVAL)!.item,
            elapsedMs: this.clock,
            steps: w.steps,
            actions: w.actions,
            bonks: w.bonks,
            explored: new Set(this.path).size,
            tier: s.watch.tiers[0],
            seed: s.seed,
          },
        });
      }
    }
    if (feed.length) {
      const sp = useGame.getState().spectator;
      if (sp) useGame.setState({ spectator: { ...sp, feed: [...sp.feed, ...feed].slice(-5) } });
    }
  }

  /** Rooms the spectator camera shows: the 3×3×3 block around the watched agent (or its room). */
  refocus(): void {
    const m = this.match;
    if (!m || this.mode !== 'watch') return;
    const s = useGame.getState();
    const room = m.agent(s.watch.follow)!.room;
    const [x, y, z] = m.maze.coords(room);
    const set = new Set<number>();
    const r = s.watch.zoom === 'in' ? 0 : 1;
    for (let dx = -r; dx <= r; dx++)
      for (let dy = -r; dy <= r; dy++)
        for (let dz = -r; dz <= r; dz++) {
          const c = m.maze.roomAt(x + dx, y + dy, z + dz);
          if (c >= 0) set.add(c);
        }
    this.visible = set;
    this.bumpScene();
  }

  /** Refresh the spectator overlay a few times a second (or now). */
  private spectate(force: boolean): void {
    const m = this.match!;
    const t = performance.now();
    if (!force && t - this.lastSpectate < 250) return;
    this.lastSpectate = t;
    const agents: SpectatorView['agents'] = {};
    for (const id of [PLAYER, RIVAL]) {
      const a = m.agent(id)!;
      const sn = m.sensorsFor(id);
      agents[id] = { item: a.item, emp: a.gear.emp, bulkhead: a.gear.bulkhead, threat: sn.threat, core: sn.core, jammed: sn.jammed, steps: a.steps };
    }
    const d = m.maze.pathDistances(m.agent(PLAYER)!.room)[m.agent(RIVAL)!.room] ?? -1;
    const sp = useGame.getState().spectator;
    useGame.setState({
      spectator: { agents, intercept: d >= 0 && d <= 3 ? d : null, cloaked: m.viewFor(PLAYER).cloaked, feed: sp?.feed ?? [] },
    });
  }

  private handle(events: MatchEvent[]): void {
    const m = this.match!;
    for (const e of events) {
      if (e.type === 'actionStarted' && e.agent === PLAYER) {
        // Online, the action is pinned to the event (see RemoteMatch.apply).
        const a = (e as typeof e & { detail?: ActiveAction }).detail ?? m.agent(PLAYER)!.active;
        if (!a) continue;
        const label =
          a.kind === 'swap' ? `SWAPPING → ${a.item.toUpperCase()}` : a.kind === 'emp' ? 'EMP' : a.kind === 'bulkhead' ? 'SEALING' : 'LOCKED';
        useGame.setState({ lock: { label, startedAt: performance.now(), durationMs: e.endsAt - e.at } });
        if (a.kind === 'rotate') audio.servo(rotationMs(a.rotation), servoPitch(a.rotation));
        if (a.kind === 'step') {
          audio.hiss();
          this.trail.add(doorKey(a.from, a.to));
          this.visible = m.maze.roomsWithin([a.from, a.to], SIGHTLINE_ROOMS);
          this.bumpScene();
        }
        if (a.kind === 'swap') audio.swap();
        if (a.kind === 'emp') audio.empFire();
      } else if (e.type === 'doorSealed') {
        if (e.agent === PLAYER || this.visible.has(e.from) || this.visible.has(e.to)) audio.seal();
      } else if (e.type === 'jammed' && e.agent === PLAYER) {
        audio.jammed();
        this.toast('SENSORS JAMMED', 'A RIVAL EMP HIT YOU · SOMEONE IS CLOSE');
      } else if (e.type === 'cachePicked') {
        if (e.agent === PLAYER) {
          useGame.setState({ pickedAt: performance.now() });
          audio.pickup();
          this.toast(`+1 ${e.kind === 'emp' ? 'EMP' : 'BULKHEAD'}`, 'SUPPLY CACHE');
        }
        this.bumpScene(); // the crate is gone
      } else if (e.type === 'bonked' && e.agent === PLAYER) {
        audio.thud();
        useGame.setState((s) => ({ bonkFlash: s.bonkFlash + 1 }));
      } else if (e.type === 'arrived' && e.agent === RIVAL) {
        this.rivalPath.push(e.room);
      } else if (e.type === 'arrived' && e.agent === PLAYER) {
        this.path.push(e.room);
        this.explored.add(e.room);
        this.visible = m.maze.roomsWithin([e.room], SIGHTLINE_ROOMS);
        this.bumpScene();
      } else if (e.type === 'cloaked') {
        audio.shimmer();
        useGame.setState((s) => ({ cloakFlash: s.cloakFlash + 1 }));
        this.toast('CLOAKED', 'SAME ITEM · YOU PASS UNSEEN');
      } else if (e.type === 'matchOver' && this.mode === 'online') {
        // The server's "over" message follows with both items.
      } else if (e.type === 'matchOver') {
        const me = m.agent(PLAYER)!;
        const s = useGame.getState();
        const won = e.result.winner === PLAYER;
        if (won) audio.fanfare();
        else audio.defeat();
        useGame.setState({
          phase: 'over',
          lock: null,
          result: {
            ...e.result,
            playerItem: me.item,
            rivalItem: m.agent(RIVAL)!.item,
            elapsedMs: s.startedAt === null ? 0 : performance.now() - s.startedAt,
            steps: me.steps,
            actions: me.actions,
            bonks: me.bonks,
            explored: this.explored.size,
            tier: s.tier,
            seed: s.seed,
          },
        });
      }
    }
    if (events.some((e) => 'agent' in e && e.agent === PLAYER)) this.syncHud();
  }

  private toast(text: string, sub: string): void {
    useGame.setState((s) => ({ toast: { text, sub, id: (s.toast?.id ?? 0) + 1 } }));
  }

  private bumpScene(): void {
    useGame.setState((s) => ({ sceneVersion: s.sceneVersion + 1 }));
  }

  private syncHud(): void {
    const m = this.match!;
    const me = m.agent(PLAYER)!;
    const [x, y, z] = m.maze.coords(me.room);
    useGame.setState({
      coords: [x, y, z],
      facing: axisLabel(me.orientation.facing),
      up: axisLabel(me.orientation.up),
      item: me.item,
      emp: me.gear.emp,
      bulkhead: me.gear.bulkhead,
      steps: me.steps,
      actions: me.actions,
      bonks: me.bonks,
    });
    this.syncSensors();
  }

  private syncSensors(): void {
    const s = this.match!.sensorsFor(PLAYER);
    const cur = useGame.getState();
    if (cur.coreBand !== s.core || cur.threatBand !== s.threat || cur.jammed !== s.jammed)
      useGame.setState({ coreBand: s.core, threatBand: s.threat, jammed: s.jammed });
  }

  /** Where an agent is drawn right now, interpolating its current animation. */
  pose(id: string, out: Pose): Pose {
    const m = this.match!;
    const agent = m.agent(id)!;
    const a = agent.active;
    const t = this.now();
    roomCenter(m.maze, agent.room, out.position);
    orientationQuat(agent.orientation, out.quaternion);
    if (!a) return out;
    const k = Math.min(1, Math.max(0, (t - a.startedAt) / (a.endsAt - a.startedAt)));
    if (a.kind === 'rotate') {
      const q0 = orientationQuat(a.from);
      out.quaternion.copy(q0).slerp(orientationQuat(a.to), ease(k));
    } else if (a.kind === 'step') {
      const p0 = roomCenter(m.maze, a.from);
      out.position.copy(p0).lerp(roomCenter(m.maze, a.to), ease(k));
    } else if (a.kind === 'bonk' && !REDUCED_MOTION) {
      const f = agent.orientation.facing;
      out.position.addScaledVector(new THREE.Vector3(f[0], f[1], f[2]), Math.sin(Math.PI * k) * 0.55);
    }
    return out;
  }
}

function servoPitch(r: Rotation): number {
  if (r === 'pitchUp' || r === 'pitchDown') return 150;
  if (r === 'rollLeft' || r === 'rollRight') return 210;
  return 180;
}

export const game = new GameController();
export { ACTION_MS };
